import React, { useState, useEffect } from 'react';
import { sessionsAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ConfirmModal from '../../components/common/ConfirmModal';
import Toast from '../../components/common/Toast';
import Pagination from '../../components/common/Pagination';

const SessionList = () => {
  const [sessionType, setSessionType] = useState('user');
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [confirmModal, setConfirmModal] = useState({ show: false, sessionId: null, userId: null, type: null });
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  useEffect(() => {
    fetchSessions();
  }, [sessionType, currentPage]);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const response = sessionType === 'user'
        ? await sessionsAPI.getUserSessions({ page: currentPage, limit: 20 })
        : await sessionsAPI.getAdminSessions({ page: currentPage, limit: 20 });

      if (response.data.success) {
        setSessions(response.data.data.sessions);
        setTotalPages(response.data.data.pagination.total_pages);
      }
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
      showToast('Failed to fetch sessions', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeSession = async (sessionId) => {
    try {
      if (sessionType === 'user') {
        await sessionsAPI.revokeUserSession(sessionId);
      } else {
        await sessionsAPI.revokeAdminSession(sessionId);
      }
      showToast('Session revoked successfully', 'success');
      fetchSessions();
    } catch (error) {
      showToast('Failed to revoke session', 'error');
    }
    setConfirmModal({ show: false, sessionId: null, userId: null, type: null });
  };

  const handleRevokeAllUserSessions = async (userId) => {
    try {
      await sessionsAPI.revokeAllUserSessions(userId);
      showToast('All user sessions revoked successfully', 'success');
      fetchSessions();
    } catch (error) {
      showToast('Failed to revoke sessions', 'error');
    }
    setConfirmModal({ show: false, sessionId: null, userId: null, type: null });
  };

  const showToast = (message, type) => {
    setToast({ show: true, message, type });
  };

  const executeConfirmedAction = () => {
    const { sessionId, userId, type } = confirmModal;
    if (type === 'single') handleRevokeSession(sessionId);
    if (type === 'all') handleRevokeAllUserSessions(userId);
  };

  return (
    <div>
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />

      <ConfirmModal
        show={confirmModal.show}
        onClose={() => setConfirmModal({ show: false, sessionId: null, userId: null, type: null })}
        onConfirm={executeConfirmedAction}
        title="Revoke Session"
        message={
          confirmModal.type === 'all'
            ? 'Are you sure you want to revoke all sessions for this user? They will be logged out from all devices.'
            : 'Are you sure you want to revoke this session? The user will be logged out from this device.'
        }
        type="warning"
      />

      <div className="page-header mb-4">
        <h2 className="page-title">Active Sessions</h2>
        <p className="page-subtitle">Monitor and manage active user sessions</p>
      </div>

      <div className="stat-card mb-4">
        <div className="btn-group">
          <button
            className={`btn ${sessionType === 'user' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => {
              setSessionType('user');
              setCurrentPage(1);
            }}
          >
            <i className="bi bi-people me-2"></i>
            User Sessions
          </button>
          <button
            className={`btn ${sessionType === 'admin' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => {
              setSessionType('admin');
              setCurrentPage(1);
            }}
          >
            <i className="bi bi-shield-check me-2"></i>
            Admin Sessions
          </button>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="data-table">
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>{sessionType === 'user' ? 'User' : 'Admin'}</th>
                  <th>Email</th>
                  {sessionType === 'admin' && <th>Role</th>}
                  <th>Created</th>
                  <th>Expires</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sessions.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      <i className="bi bi-inbox fs-1 text-muted d-block mb-2"></i>
                      <p className="text-muted mb-0">No active sessions</p>
                    </td>
                  </tr>
                ) : (
                  sessions.map((session) => (
                    <tr key={session.id}>
                      <td>
                        {sessionType === 'user' ? session.user_name : session.admin_name}
                      </td>
                      <td>
                        <small>
                          {sessionType === 'user' ? session.user_email : session.admin_email}
                        </small>
                      </td>
                      {sessionType === 'admin' && (
                        <td>
                          <span className="badge badge-purple">
                            {session.role?.replace('_', ' ')}
                          </span>
                        </td>
                      )}
                      <td>
                        <small className="text-muted">
                          {new Date(session.created_at).toLocaleString()}
                        </small>
                      </td>
                      <td>
                        <small className="text-muted">
                          {new Date(session.expires_at).toLocaleString()}
                        </small>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn btn-sm btn-outline-warning"
                            onClick={() => setConfirmModal({
                              show: true,
                              sessionId: session.id,
                              userId: null,
                              type: 'single'
                            })}
                            title="Revoke Session"
                          >
                            <i className="bi bi-x-circle"></i>
                          </button>
                          {sessionType === 'user' && (
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => setConfirmModal({
                                show: true,
                                sessionId: null,
                                userId: session.user_id,
                                type: 'all'
                              })}
                              title="Revoke All User Sessions"
                            >
                              <i className="bi bi-ban"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="p-3 border-top">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SessionList;